import React, { useEffect, useState, useCallback } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";
import { setToast } from "../../../util/toast";

// Howyin palette
const INK = "#1A1614";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const HAIR = "#E8E1D3";
const MUTED = "#8A857F";

const STATE = {
  live: { label: "Live now", bg: "#E7F6EC", fg: "#16A34A" },
  scheduled: { label: "Scheduled", bg: "#EAF2FF", fg: "#2563EB" },
  ended: { label: "Ended", bg: "#F1EEE7", fg: MUTED },
  off: { label: "Switched off", bg: "#FDEAEA", fg: "#DC2626" },
};

const input = { padding: "9px 12px", borderRadius: 10, border: `1px solid ${HAIR}`, fontSize: 13, background: CREAM, width: "100%" };
const label = { fontSize: 12, color: MUTED, fontWeight: 600, marginBottom: 4, display: "block" };
const btn = (bg, fg = "#fff") => ({ padding: "9px 16px", borderRadius: 10, border: "none", background: bg, color: fg, fontWeight: 600, fontSize: 13, cursor: "pointer" });
const toLocal = (d) => (d ? dayjs(d).format("YYYY-MM-DDTHH:mm") : "");
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

const emptyForm = () => ({
  _id: null,
  name: "",
  startsAt: toLocal(dayjs().startOf("day")),
  endsAt: toLocal(dayjs().add(7, "day").endOf("day")),
  isActive: true,
  limitOn: false,
  perUserLimit: 1,
  rows: {}, // packageId -> { on, bonusType, bonusValue }
});

// Admin → Coin Bonus (2026-10-04): festive / anniversary bonuses on coin packs.
const CoinBonus = () => {
  const [promotions, setPromotions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [quick, setQuick] = useState({ bonusType: "flat", bonusValue: "" });

  const load = useCallback(async () => {
    try {
      const res = await apiInstanceFetch.get("coinPromotion/list");
      if (res && res.status) {
        setPromotions(res.promotions || []);
        setPackages(res.packages || []);
      }
    } catch (e) {
      setToast("error", "Could not load promotions");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => setForm(emptyForm());
  const openEdit = (p) => {
    const rows = {};
    (p.items || []).forEach((i) => {
      rows[String(i.packageId)] = { on: true, bonusType: i.bonusType, bonusValue: i.bonusValue };
    });
    setForm({
      _id: p._id,
      name: p.name,
      startsAt: toLocal(p.startsAt),
      endsAt: toLocal(p.endsAt),
      isActive: p.isActive,
      limitOn: (p.perUserLimit || 0) > 0,
      perUserLimit: p.perUserLimit || 1,
      rows,
    });
  };

  const setRow = (id, patch) =>
    setForm((f) => ({ ...f, rows: { ...f.rows, [id]: { on: false, bonusType: "flat", bonusValue: "", ...f.rows[id], ...patch } } }));

  const applyToAll = () => {
    if (!(Number(quick.bonusValue) > 0)) return setToast("error", "Enter a bonus amount first");
    setForm((f) => {
      const rows = { ...f.rows };
      packages.filter((p) => p.isActive).forEach((p) => {
        rows[p._id] = { on: true, bonusType: quick.bonusType, bonusValue: quick.bonusValue };
      });
      return { ...f, rows };
    });
  };

  const preview = (pkg, row) => {
    if (!row || !row.on || !(Number(row.bonusValue) > 0)) return "—";
    const b = row.bonusType === "percent" ? Math.floor((pkg.coins * Number(row.bonusValue)) / 100) : Math.floor(Number(row.bonusValue));
    return `${fmt(pkg.coins)} + ${fmt(b)} = ${fmt(pkg.coins + b)} coins`;
  };

  const save = async () => {
    if (!form.name.trim()) return setToast("error", "Give the promotion a name");
    const items = Object.entries(form.rows)
      .filter(([, r]) => r.on && Number(r.bonusValue) > 0)
      .map(([packageId, r]) => ({ packageId, bonusType: r.bonusType, bonusValue: Number(r.bonusValue) }));
    if (!items.length) return setToast("error", "Tick at least one coin pack and enter its bonus");
    const body = {
      name: form.name.trim(),
      startsAt: new Date(form.startsAt).toISOString(),
      endsAt: new Date(form.endsAt).toISOString(),
      isActive: form.isActive,
      perUserLimit: form.limitOn ? Math.max(1, parseInt(form.perUserLimit) || 1) : 0,
      items,
    };
    setSaving(true);
    try {
      const res = form._id
        ? await apiInstanceFetch.patch(`coinPromotion/update?promotionId=${form._id}`, body)
        : await apiInstanceFetch.post("coinPromotion/create", body);
      if (res && res.status) {
        setToast("success", res.message || "Saved");
        setForm(null);
        load();
      } else {
        setToast("error", (res && res.message) || "Could not save");
      }
    } catch (e) {
      setToast("error", "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (p) => {
    const body = {
      name: p.name,
      startsAt: p.startsAt,
      endsAt: p.endsAt,
      isActive: !p.isActive,
      perUserLimit: p.perUserLimit || 0,
      items: p.items,
    };
    const res = await apiInstanceFetch.patch(`coinPromotion/update?promotionId=${p._id}`, body).catch(() => null);
    if (res && res.status) {
      setToast("success", p.isActive ? "Promotion switched off" : "Promotion switched on");
      load();
    } else setToast("error", (res && res.message) || "Could not change it");
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    const res = await apiInstanceFetch.delete(`coinPromotion/delete?promotionId=${p._id}`).catch(() => null);
    if (res && res.status) {
      setToast("success", res.message || "Deleted");
      load();
    } else setToast("error", (res && res.message) || "Could not delete");
  };

  const th = { textAlign: "left", padding: "10px 12px", fontSize: 12, color: MUTED, fontWeight: 600, borderBottom: `1px solid ${HAIR}`, whiteSpace: "nowrap" };
  const td = { padding: "10px 12px", fontSize: 13, color: INK, borderBottom: `1px solid ${HAIR}`, verticalAlign: "middle" };

  return (
    <div style={{ padding: 16 }}>
      <Title name="Coin Bonus" />
      <p style={{ color: MUTED, fontSize: 13, margin: "6px 0 14px" }}>
        Give extra coins on coin pack purchases for a set period, such as an anniversary or festive season. Only one promotion can run at a time.
        Bonus coins are added automatically after payment, on every payment method.
      </p>

      {!form ? (
        <button style={btn(GOLD)} onClick={openNew}>
          + New promotion
        </button>
      ) : (
        <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, padding: 16, marginTop: 6 }}>
          <div style={{ fontWeight: 700, color: INK, marginBottom: 12 }}>{form._id ? "Edit promotion" : "New promotion"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
            <div>
              <span style={label}>Name shown to users</span>
              <input style={input} value={form.name} maxLength={60} placeholder="e.g. Anniversary Bonus" onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <span style={label}>Starts</span>
              <input style={input} type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} />
            </div>
            <div>
              <span style={label}>Ends</span>
              <input style={input} type="datetime-local" value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} />
            </div>
            <div>
              <span style={label}>Limit per person</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select style={{ ...input, width: "auto" }} value={form.limitOn ? "on" : "off"} onChange={(e) => setForm({ ...form, limitOn: e.target.value === "on" })}>
                  <option value="off">No limit (every purchase)</option>
                  <option value="on">Limit to</option>
                </select>
                {form.limitOn ? (
                  <>
                    <input style={{ ...input, width: 70 }} type="number" min={1} value={form.perUserLimit} onChange={(e) => setForm({ ...form, perUserLimit: e.target.value })} />
                    <span style={{ fontSize: 12, color: MUTED }}>purchases</span>
                  </>
                ) : null}
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "16px 0 8px" }}>
            <span style={{ fontSize: 12, color: MUTED, fontWeight: 600 }}>Quick fill every active pack:</span>
            <select style={{ ...input, width: "auto" }} value={quick.bonusType} onChange={(e) => setQuick({ ...quick, bonusType: e.target.value })}>
              <option value="flat">+ coins</option>
              <option value="percent">+ % extra</option>
            </select>
            <input style={{ ...input, width: 110 }} type="number" min={0} placeholder={quick.bonusType === "flat" ? "e.g. 500" : "e.g. 50"} value={quick.bonusValue} onChange={(e) => setQuick({ ...quick, bonusValue: e.target.value })} />
            <button style={btn("#fff", INK)} onClick={applyToAll}>
              Apply to all
            </button>
          </div>

          <div style={{ border: `1px solid ${HAIR}`, borderRadius: 12, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
              <thead>
                <tr>
                  <th style={th}>Include</th>
                  <th style={th}>Coin pack</th>
                  <th style={th}>Price</th>
                  <th style={th}>Bonus</th>
                  <th style={th}>Buyer receives</th>
                </tr>
              </thead>
              <tbody>
                {packages.map((p) => {
                  const r = form.rows[p._id] || { on: false, bonusType: "flat", bonusValue: "" };
                  return (
                    <tr key={p._id} style={{ opacity: p.isActive ? 1 : 0.5 }}>
                      <td style={td}>
                        <input type="checkbox" checked={!!r.on} onChange={(e) => setRow(p._id, { on: e.target.checked })} />
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{p.name}</div>
                        {!p.isActive ? <div style={{ fontSize: 11, color: MUTED }}>pack is inactive</div> : null}
                      </td>
                      <td style={td}>${Number(p.price).toFixed(2)}</td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <select style={{ ...input, width: "auto" }} value={r.bonusType} onChange={(e) => setRow(p._id, { bonusType: e.target.value, on: true })}>
                            <option value="flat">+ coins</option>
                            <option value="percent">+ %</option>
                          </select>
                          <input style={{ ...input, width: 100 }} type="number" min={0} value={r.bonusValue} onChange={(e) => setRow(p._id, { bonusValue: e.target.value, on: true })} />
                        </div>
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: r.on ? GOLD : MUTED }}>{preview(p, r)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 14 }}>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13, color: INK }}>
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Switched on
            </label>
            <div style={{ flex: 1 }} />
            <button style={btn("#fff", INK)} onClick={() => setForm(null)}>
              Cancel
            </button>
            <button style={btn(GOLD)} onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save promotion"}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "auto", marginTop: 16 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr>
              <th style={th}>Promotion</th>
              <th style={th}>Status</th>
              <th style={th}>Runs</th>
              <th style={th}>Packs</th>
              <th style={th}>Limit</th>
              <th style={th}>Bonus given</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {promotions.length === 0 ? (
              <tr>
                <td style={{ ...td, textAlign: "center", color: MUTED }} colSpan={7}>
                  No promotions yet.
                </td>
              </tr>
            ) : (
              promotions.map((p) => {
                const st = STATE[p.state] || STATE.off;
                return (
                  <tr key={p._id}>
                    <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                    <td style={td}>
                      <span style={{ background: st.bg, color: st.fg, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10 }}>{st.label}</span>
                    </td>
                    <td style={td}>
                      <div>{dayjs(p.startsAt).format("DD MMM YYYY, HH:mm")}</div>
                      <div style={{ fontSize: 11, color: MUTED }}>to {dayjs(p.endsAt).format("DD MMM YYYY, HH:mm")}</div>
                    </td>
                    <td style={td}>{(p.items || []).length}</td>
                    <td style={td}>{p.perUserLimit > 0 ? `${p.perUserLimit} per person` : "No limit"}</td>
                    <td style={td}>
                      <div style={{ fontWeight: 700, color: GOLD }}>{fmt(p.stats && p.stats.bonusCoins)} coins</div>
                      <div style={{ fontSize: 11, color: MUTED }}>
                        {fmt(p.stats && p.stats.purchases)} purchases · {fmt(p.stats && p.stats.buyers)} buyers
                      </div>
                    </td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>
                      <button style={{ ...btn("#fff", INK), padding: "6px 10px" }} onClick={() => openEdit(p)}>
                        Edit
                      </button>{" "}
                      <button style={{ ...btn("#fff", INK), padding: "6px 10px" }} onClick={() => toggle(p)}>
                        {p.isActive ? "Switch off" : "Switch on"}
                      </button>{" "}
                      <button style={{ ...btn("#fff", "#DC2626"), padding: "6px 10px" }} onClick={() => remove(p)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CoinBonus;
